import {
  getDefaultsPvSystem,
  useSite,
  useSiteFileUpload,
} from "@inrange/building-manager-api-client";
import {
  AllowStringForNumbers,
  ChangeToSiteDefaults,
  EditMode,
  InstalledInverter,
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
  SiteElectricalSystem,
  SiteElectricalSystemPlaceholder,
  SiteFile,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { ChangeEvent } from "react";
import { Button, Col, Form, Row, Stack } from "react-bootstrap";
import CollapsibleCard from "../CollapsibleCard";
import PanelOrientationsEditor from "./PanelOrientationsEditor";
import {
  isEmptyDiff,
  isSafeToApplyNewDefaults,
  selectChangedDefaults,
} from "../confirm-changes-utils";
import Inverters from "./Inverters";
import LossFactors from "./LossFactors";
import { OPERATIONAL_STATUSES } from "../operationalStatusUtils";
import {
  formatNumber,
  formatNumber2dp,
  inverterRatedOutputPowerTotalKwpAc,
  parseAsNumber,
  roundToDp,
  setMissingValuesZeroAndParseStringsToNumbers,
} from "./utils";

const Generation = ({
  site,
  esys,
  matchSuffix,
  setSite,
  setPendingUpdateSite,
  siteFiles,
  onFileAdded,
  onFileDeleted,
  setSaveDisabled,
  pvDesignInstalledCapacity,
  pvDesignUploadRef,
  isBulkEdit,
  editMode,
}: {
  site: SiteAllowStringValues;
  esys:
    | AllowStringForNumbers<SiteElectricalSystem>
    | SiteElectricalSystemPlaceholder;
  matchSuffix?: string;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  setPendingUpdateSite: (updates: ChangeToSiteDefaults[] | undefined) => void;
  siteFiles?: SiteFile[];
  onFileAdded?: (file: SiteFile) => void;
  onFileDeleted?: (file: SiteFile) => void;
  setSaveDisabled?: (disabled: boolean) => void;
  pvDesignInstalledCapacity?: number;
  pvDesignUploadRef?: React.RefObject<HTMLInputElement | null>;
  isBulkEdit?: boolean;
  editMode: EditMode;
}) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);
  const { fetchSiteValues } = useSite({
    app: "admin",
    enableNonEssentialQueries: true,
  });

  // Default these here to allow for bulk edit
  const pvInverterBrand =
    site.pvInverterBrand ?? fetchSiteValues.data!.site.pvInverterBrand;
  const pvPanelName =
    site.pvPanelName ?? fetchSiteValues.data!.site.pvPanelName;

  const { uploadSiteFile } = useSiteFileUpload(site.id);
  const currentPvDesignImage = siteFiles?.find(
    (file) => file.fileType === "pvDesignImage"
  );

  const showPvDesignImageUpload =
    onFileAdded && onFileDeleted && setSaveDisabled;
  const onPvDesignImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      setSaveDisabled!(true);
      const newPvDesignImage = {
        fileType: "pvDesignImage",
        fileGuid: null,
        fileName: file.name,
        description: "PV Design Image",
        isPublished: true,
      };
      uploadSiteFile.mutate(
        {
          file,
          siteFile: newPvDesignImage,
        },
        {
          onSettled: () => setSaveDisabled!(false),
          onSuccess: (data) => {
            onFileAdded &&
              onFileAdded({
                ...newPvDesignImage,
                fileGuid: data.fileGuid,
                fileName: data.fileName,
              } as any);
          },
          onError: (error) => {
            console.error("Error uploading PV design image", error);
          },
        }
      );
    }
  };

  const siteLossFactorsRaw = site.generationLossFactors || {};

  const onPvHardwareChange = (
    newPvInverterBrand: string,
    newPvPanelName: string
  ) => {
    const previousPvSystemDefaults = getDefaultsPvSystem(
      fetchSiteValues.data!.options.pvPanels,
      fetchSiteValues.data!.options.pvSystems,
      siteCalculations.currencyCode,
      pvInverterBrand,
      pvPanelName
    );
    const pvSystem = getDefaultsPvSystem(
      fetchSiteValues.data!.options.pvPanels,
      fetchSiteValues.data!.options.pvSystems,
      siteCalculations.currencyCode,
      newPvInverterBrand,
      newPvPanelName
    );

    // Work out how to apply loss factor changes
    const prevDefaultLossFactors = {
      ...previousPvSystemDefaults.generationLossFactors,
    };
    const newDefaultLossFactors = {
      ...pvSystem.generationLossFactors,
    };
    const siteLossFactors = { ...siteLossFactorsRaw };
    setMissingValuesZeroAndParseStringsToNumbers(
      prevDefaultLossFactors,
      siteLossFactors
    );
    setMissingValuesZeroAndParseStringsToNumbers(
      newDefaultLossFactors,
      siteLossFactors
    );
    const lossFactorDiffs = selectChangedDefaults(
      { generationLossFactors: prevDefaultLossFactors },
      { generationLossFactors: newDefaultLossFactors }
    );
    const canUpdateLossFactors =
      isEmptyDiff(lossFactorDiffs) ||
      isSafeToApplyNewDefaults(site, lossFactorDiffs);

    // Work out how to apply cost input changes
    const costInputsPvDiff = selectChangedDefaults(
      { costInputsPv: previousPvSystemDefaults.costInputsPv },
      { costInputsPv: pvSystem.costInputsPv }
    );
    const canUpdateCostInputs =
      isEmptyDiff(costInputsPvDiff) ||
      isSafeToApplyNewDefaults(site, costInputsPvDiff);

    // Apply safe changes
    setSite({
      ...(newPvInverterBrand !== pvInverterBrand && {
        pvInverterBrand: newPvInverterBrand,
      }),
      ...(newPvPanelName !== pvPanelName && {
        pvPanelName: newPvPanelName,
      }),
      ...(canUpdateLossFactors && {
        generationLossFactors: {
          ...siteLossFactorsRaw,
          ...lossFactorDiffs.afterDefaults.generationLossFactors,
        },
      }),
      ...(canUpdateCostInputs && {
        costInputsPv: {
          ...site.costInputsPv,
          ...costInputsPvDiff.afterDefaults.costInputsPv,
        },
      }),
    });

    // Prompt for any unsafe changes
    if (!canUpdateLossFactors || !canUpdateCostInputs) {
      setPendingUpdateSite([
        ...(canUpdateCostInputs
          ? []
          : [
              {
                title: "Costs and rates",
                ...costInputsPvDiff,
              },
            ]),
        ...(canUpdateLossFactors
          ? []
          : [
              {
                title: "Loss Factors",
                ...lossFactorDiffs,
              },
            ]),
      ]);
    }
  };

  const generationPr =
    siteCalculations.aggHalfHourlyGeneration &&
    siteCalculations.aggHalfHourlyGeneration.genStack.stack[0].after
      ? (siteCalculations.aggHalfHourlyGeneration.genStack.stack[
          siteCalculations.aggHalfHourlyGeneration.genStack.stack.length - 1
        ].after /
          (siteCalculations.aggHalfHourlyGeneration.genStack.stack[0].after /
            siteCalculations.aggHalfHourlyGeneration.yoyVarInvLossFactor)) *
        100
      : 0;

  const installedCapacity =
    editMode === "SUMMARY"
      ? siteCalculations.totalInstalledCapacity
      : site.generation[esys.genIdx]?.installedCapacity;

  const inverters: InstalledInverter[] =
    editMode === "SUMMARY"
      ? siteCalculations.totalInverters
      : site.generation[esys.genIdx]?.inverterConfig !== "manual"
        ? siteCalculations.generation?.[esys.genIdx]?.inverters || []
        : site.generation[esys.genIdx]?.inverters || [];

  const pnomRatio = roundToDp(
    parseAsNumber(installedCapacity) /
      inverterRatedOutputPowerTotalKwpAc(
        fetchSiteValues.data!.options.pvInverters,
        inverters
      ),
    2
  );

  const energyFlowAnnual =
    editMode === "SUMMARY"
      ? siteCalculations.energyFlowAnnual
      : siteCalculations?.esys?.[esys.idx]?.energyFlowAnnual;

  const pvDesignInstalledCapacityError =
    !!pvDesignInstalledCapacity &&
    pvDesignInstalledCapacity !==
      parseAsNumber(
        editMode === "SUMMARY"
          ? siteCalculations.totalInstalledCapacity
          : site.generation[esys.genIdx]?.installedCapacity
      );

  const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;

  return (
    <CollapsibleCard
      title={`Generation${matchSuffix || ""}`}
      className="mt-2"
      id="generation-section"
      summary={
        isBulkEdit
          ? undefined
          : `${formatNumber(
              editMode === "SUMMARY"
                ? siteCalculations.totalInstalledCapacity
                : site.generation[esys.genIdx]?.installedCapacity,
              3,
              3,
              false
            )} kWp - ${formatNumber2dp(energyFlowAnnual?.generation || 0)} kWh - ${site.pvForecastProbability}`
      }
      initialOpen={editMode === "SINGLE"}
    >
      <Row className="d-flex flex-wrap align-items-end">
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`installedCapacity-${esys.genIdx}`}
          >
            <Form.Label>Installed capacity (kWp)</Form.Label>
            <Form.Control
              type="text"
              value={formatNumber(installedCapacity, 3, 3, false)}
              onChange={(e) =>
                setSite({
                  generation: {
                    [esys.genIdx]: {
                      installedCapacity: formatNumber(
                        e.target.value,
                        3,
                        3,
                        false
                      ),
                    },
                  },
                })
              }
              disabled={editMode === "SUMMARY"}
              isInvalid={
                !!errors[`generation.${esys.genIdx}.installedCapacity`]
              }
              data-testid={"installedCapacity"}
            />
            {!errors[`generation.${esys.genIdx}.installedCapacity`] &&
              pvDesignInstalledCapacityError && (
                <div
                  style={{
                    fontSize: "0.875em",
                    marginTop: "0.25rem",
                  }}
                >
                  vs {formatNumber(pvDesignInstalledCapacity, 3, 3, false)}{" "}
                  auto-design
                </div>
              )}
            <Form.Control.Feedback type="invalid">
              {errors[`generation.${esys.genIdx}.installedCapacity`]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        {editMode !== "ONE_OF_MULTIPLE" && (
          <>
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`pvForecastProbability-${esys.genIdx}`}
              >
                <Form.Label>Forecast probability</Form.Label>
                <Form.Select
                  value={site.pvForecastProbability}
                  onChange={(e) =>
                    setSite({
                      pvForecastProbability: e.target.value,
                    })
                  }
                >
                  <option>P50</option>
                  <option>P90</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`pvInverterBrand-${esys.genIdx}`}
              >
                <Form.Label>Inverters used</Form.Label>
                <Form.Select
                  value={pvInverterBrand}
                  onChange={(e) =>
                    onPvHardwareChange(e.target.value, pvPanelName)
                  }
                >
                  {fetchSiteValues.data!.options.pvInverterBrands.map(
                    (inverterBrand, index) => (
                      <option key={index}>{inverterBrand}</option>
                    )
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group className="mb-3" controlId={`pvPanel-${esys.genIdx}`}>
                <Form.Label>Panel used</Form.Label>
                <Form.Select
                  value={pvPanelName}
                  onChange={(e) =>
                    onPvHardwareChange(pvInverterBrand, e.target.value)
                  }
                >
                  {Object.entries(fetchSiteValues.data!.options.pvPanels)
                    .filter(
                      ([panelName, panel]) =>
                        !panel.deprecated ||
                        panelName === pvPanelName ||
                        testMode
                    )
                    .map(([panelName], index) => (
                      <option key={index}>{panelName}</option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`panelCount-${esys.genIdx}`}
              >
                <Form.Label>Panel count</Form.Label>
                <Form.Control
                  disabled
                  value={
                    formatNumber(siteCalculations.pvPanelCount, 0, 0) ?? ""
                  }
                  data-testid={"panelCount"}
                />
              </Form.Group>
            </Col>
          </>
        )}
        {!isBulkEdit && (
          <>
            {editMode !== "SUMMARY" && (
              <>
                <Col sm={4}>
                  <Form.Group
                    className="mb-3"
                    controlId={`roofUtilisation-${esys.genIdx}`}
                  >
                    <Form.Label>Roof utilisation (%)</Form.Label>
                    <Form.Control
                      disabled
                      value={formatNumber2dp(
                        (siteCalculations.generation?.[esys.genIdx]
                          ?.halfHourlyGeneration?.roofUtilization ?? 0) * 100
                      )}
                      data-testid={"roofUtil"}
                    />
                  </Form.Group>
                </Col>
              </>
            )}
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`annualGeneration-${esys.genIdx}`}
              >
                <Form.Label>Annual generation (kWh)</Form.Label>
                <Form.Control
                  disabled
                  value={formatNumber2dp(energyFlowAnnual?.generation || 0)}
                  data-testid={`annualGeneration-${esys.genIdx}`}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`consumedBhm-${esys.genIdx}`}
              >
                <Form.Label>Consumed on-site (kWh)</Form.Label>
                <Form.Control
                  disabled
                  value={formatNumber2dp(energyFlowAnnual?.behindMeter || 0)}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`exported-${esys.genIdx}`}
              >
                <Form.Label>Exported (matched) (kWh)</Form.Label>
                <Form.Control
                  disabled
                  value={formatNumber2dp(energyFlowAnnual?.networkExport || 0)}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`exportedSpill-${esys.genIdx}`}
              >
                <Form.Label>Exported (spill) (kWh)</Form.Label>
                <Form.Control
                  disabled
                  value={formatNumber2dp(energyFlowAnnual?.exported || 0)}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`curtailed-${esys.genIdx}`}
              >
                <Form.Label>Curtailed (kWh)</Form.Label>
                <Form.Control
                  disabled
                  value={formatNumber2dp(energyFlowAnnual?.curtailed || 0)}
                  data-testid={`curtailed-${esys.genIdx}`}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group className="mb-3" controlId={`pr-${esys.genIdx}`}>
                <Form.Label>Performance ratio (%)</Form.Label>
                <Form.Control disabled value={formatNumber2dp(generationPr)} />
              </Form.Group>
            </Col>
            {inverters.length > 0 && (
              <>
                <Col sm={4}>
                  <Form.Group controlId={`inverterPnomTotal-${esys.genIdx}`}>
                    <Form.Label>Inverter Pnom total (kWac)</Form.Label>
                    <Form.Control
                      disabled
                      value={formatNumber(
                        inverterRatedOutputPowerTotalKwpAc(
                          fetchSiteValues.data!.options.pvInverters,
                          inverters
                        ),
                        0,
                        3,
                        false
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group controlId={`inverterPnomRatio-${esys.genIdx}`}>
                    <Form.Label>Pnom ratio (DC:AC)</Form.Label>
                    <Form.Control
                      disabled
                      value={formatNumber(pnomRatio, 0, 2, false)}
                      isInvalid={
                        editMode !== "SUMMARY" &&
                        (pnomRatio >
                          parseAsNumber(
                            site.generation[esys.genIdx]
                              ?.inverterTargetAcDcRatioMax
                          ) ||
                          pnomRatio <
                            parseAsNumber(
                              site.generation[esys.genIdx]
                                ?.inverterTargetAcDcRatioMin
                            ))
                      }
                    />
                  </Form.Group>
                </Col>
              </>
            )}
            {editMode !== "ONE_OF_MULTIPLE" && showPvDesignImageUpload && (
              <Col md="auto">
                <Form.Group
                  className="mb-3"
                  style={{ display: "flex", flexDirection: "column" }}
                  controlId={`pvDesignImage-${esys.genIdx}`}
                >
                  <Form.Label>Designed PV image</Form.Label>
                  <Form.Control
                    type="file"
                    style={{ display: "none" }}
                    accept="image/*, application/pdf"
                    ref={pvDesignUploadRef}
                    onChange={onPvDesignImageUpload}
                  />
                  <Stack direction="horizontal" gap={3}>
                    <label
                      htmlFor={`pvDesignImage-${esys.genIdx}`}
                      className="btn btn-success"
                      data-testid={"pvDesignImageUploadButton"}
                    >
                      Choose file
                    </label>
                    <div data-testid={"pvDesignImageUploadFileName"}>
                      {!uploadSiteFile.isLoading &&
                        (currentPvDesignImage?.fileName ||
                          "No file chosen")}{" "}
                      {currentPvDesignImage &&
                        !currentPvDesignImage.fileVersionId &&
                        " (unsaved)"}
                    </div>
                    <div>
                      {uploadSiteFile.isLoading && <>(Uploading...)</>}
                      {currentPvDesignImage?.fileName &&
                        !uploadSiteFile.isLoading && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => onFileDeleted(currentPvDesignImage)}
                          >
                            Delete file
                          </Button>
                        )}
                    </div>
                  </Stack>
                </Form.Group>
              </Col>
            )}
          </>
        )}
      </Row>
      {editMode !== "ONE_OF_MULTIPLE" && (
        <LossFactors
          site={site}
          esys={esys}
          setSite={setSite}
          pvInverterBrand={pvInverterBrand}
          pvPanelName={pvPanelName}
          isBulkEdit={isBulkEdit}
          siteLossFactorsRaw={siteLossFactorsRaw}
        />
      )}
      {editMode !== "SUMMARY" && (
        <Inverters
          site={site}
          esys={esys}
          setSite={setSite}
          pvInverterBrand={pvInverterBrand}
          isBulkEdit={isBulkEdit}
          editMode={editMode}
        />
      )}
      {!isBulkEdit &&
        editMode !== "SUMMARY" &&
        OPERATIONAL_STATUSES.has(site.operationalStatus) && (
          <PanelOrientationsEditor
            orientations={site.generation[esys.genIdx]?.panelOrientations || []}
            genIdx={esys.genIdx}
            esysPanelCount={
              fetchSiteValues.data!.options.pvPanels[pvPanelName]?.pnom
                ? Math.round(
                    ((site.generation[esys.genIdx]?.installedCapacity ?? 0) *
                      1000) /
                      fetchSiteValues.data!.options.pvPanels[pvPanelName].pnom
                  )
                : 0
            }
            onChange={(orientations) =>
              setSite({
                generation: {
                  [esys.genIdx]: { panelOrientations: orientations },
                },
              })
            }
          />
        )}
    </CollapsibleCard>
  );
};

export default Generation;
